/**
 * 全局错误边界 — 捕获组件渲染异常，展示品牌化错误页而非白屏
 */

import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[Hump] ErrorBoundary caught:', error, errorInfo);
  }

  handleReload = () => {
    window.location.hash = '#/shelf';
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-warm-white dark:bg-warm-dark flex flex-col items-center justify-center gap-6 p-6">
          <p className="text-6xl">🐪</p>
          <div className="text-center">
            <h1 className="text-xl font-serif text-camel-dark mb-2">出了点问题</h1>
            <p className="text-sm text-charcoal/60 dark:text-charcoal-light/60 max-w-md">
              应用遇到了意外错误。你的数据不会丢失（存储在本地）。
              <br />
              尝试刷新页面，或返回书架重新开始。
            </p>
          </div>
          {this.state.error && (
            <details className="max-w-lg w-full">
              <summary className="text-xs text-charcoal/40 dark:text-charcoal-light/40 cursor-pointer">
                技术详情
              </summary>
              <pre className="mt-2 p-3 rounded-md bg-warm-white dark:bg-border-warm-dark text-xs text-error overflow-x-auto whitespace-pre-wrap">
                {this.state.error.message}
                {this.state.error.stack?.slice(0, 500)}
              </pre>
            </details>
          )}
          <button
            onClick={this.handleReload}
            className="px-6 py-2.5 rounded-md bg-camel text-white font-medium hover:bg-camel-light transition-colors"
          >
            返回书架
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
